import React, { useEffect, useState } from 'react';
import { getProject, submitGateDecision } from '../api/client';
import { ProjectRow } from '../../lib/types';
import AssessmentForm from '../components/assessment-form';

export interface IdeaDetailProps {
  projectId: string;
  onClose: () => void;
  onUpdate: () => void;
}

export default function IdeaDetail({ projectId, onClose, onUpdate }: IdeaDetailProps) {
  const [project, setProject] = useState<ProjectRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAssessmentForm, setShowAssessmentForm] = useState(false);
  const [showGateDecision, setShowGateDecision] = useState(false);
  const [gateDecision, setGateDecision] = useState<'Approved' | 'Rejected'>('Approved');
  const [priority, setPriority] = useState<'P1' | 'P2' | 'P3' | 'P4'>('P2');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchProject();
  }, [projectId]);

  const fetchProject = async () => {
    try {
      setLoading(true);
      const response = await getProject(projectId);
      if (response.success) {
        setProject(response.data as ProjectRow);
      }
    } catch (err) {
      console.error('Failed to fetch project:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAssessmentSuccess = () => {
    setShowAssessmentForm(false);
    onUpdate();
    fetchProject();
  };

  const handleGateDecision = async (gateNumber: 2 | 3) => {
    try {
      setError('');
      const response = await submitGateDecision({
        projectId,
        gateNumber,
        decision: gateDecision,
        approvedByEmail: 'leader@example.com',
        approvedByName: 'Leadership',
        priority: gateNumber === 3 ? priority : undefined,
      });

      if (response.success) {
        setShowGateDecision(false);
        onUpdate();
        fetchProject();
      } else {
        setError(response.error || 'Failed to make decision');
      }
    } catch (err: any) {
      setError(err.message || 'Error');
    }
  };

  const handleDiscoveryStatusUpdate = async (newStatus: string) => {
    try {
      setError('');
      const response = await fetch(`/projects/${projectId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ Status: newStatus }),
      });
      const data = await response.json();
      if (data.success) {
        onUpdate();
        fetchProject();
      } else {
        setError(data.error || 'Failed to update status');
      }
    } catch (err: any) {
      setError(err.message || 'Error');
    }
  };

  if (loading) {
    return <div className="modal-overlay"><div className="modal-content">Loading...</div></div>;
  }

  if (!project) {
    return <div className="modal-overlay"><div className="modal-content">Project not found</div></div>;
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{project.Title}</h2>
          <button onClick={onClose} className="btn-close">×</button>
        </div>

        <div className="modal-body">
          <div className="project-details">
            <div className="detail-row">
              <span className="label">ID:</span>
              <span className="value">{project.ProjectId}</span>
            </div>
            <div className="detail-row">
              <span className="label">Status:</span>
              <span className="value status">{project.Status}</span>
            </div>
            <div className="detail-row">
              <span className="label">Type:</span>
              <span className="value">{project.WorkType}</span>
            </div>
            <div className="detail-row">
              <span className="label">Domain:</span>
              <span className="value">{project.Domain}</span>
            </div>
            <div className="detail-row full">
              <span className="label">Description:</span>
              <p className="value">{project.Description}</p>
            </div>
          </div>

          {/* Discovery phase actions */}
          {project.Status === 'NewIdea' ? (
            <button onClick={() => handleDiscoveryStatusUpdate('GatheringFeedback')} className="btn-primary">
              Move to Gathering Feedback
            </button>
          ) : project.Status === 'GatheringFeedback' ? (
            <button onClick={() => handleDiscoveryStatusUpdate('ReadyForGovernance')} className="btn-primary">
              Mark as Ready for Governance
            </button>
          ) : null}

          {/* Governance phase actions */}
          {project.Status === 'ReadyForGovernance' || project.Status === 'UnderGovernanceReview' ? (
            !showAssessmentForm ? (
              <button onClick={() => setShowAssessmentForm(true)} className="btn-primary">
                Submit Governance Assessment
              </button>
            ) : (
              <AssessmentForm
                projectId={projectId}
                onSuccess={handleAssessmentSuccess}
                onCancel={() => setShowAssessmentForm(false)}
              />
            )
          ) : project.Status === 'AboveTheLine' ? (
            !showGateDecision ? (
              <button onClick={() => setShowGateDecision(true)} className="btn-primary">
                Leadership: Gate 2a Decision (Commit to Phase)
              </button>
            ) : (
              <div className="gate-decision-form">
                <h3>Gate 2a: Commit to Validation/Business Case</h3>
                <div className="form-group">
                  <label>Decision:</label>
                  <select value={gateDecision} onChange={(e) => setGateDecision(e.target.value as 'Approved' | 'Rejected')}>
                    <option value="Approved">Approve</option>
                    <option value="Rejected">Reject</option>
                  </select>
                </div>
                {error && <div className="error-message">{error}</div>}
                <div className="form-actions">
                  <button onClick={() => handleGateDecision(2)} className="btn-primary">Submit Gate 2a</button>
                  <button onClick={() => setShowGateDecision(false)} className="btn-secondary">Cancel</button>
                </div>
              </div>
            )
          ) : project.Status === 'InValidation' || project.Status === 'InPlanning' ? (
            !showGateDecision ? (
              <button onClick={() => setShowGateDecision(true)} className="btn-primary">
                Leadership: Gate 2b Decision (Commit to Work + Priority Lock)
              </button>
            ) : (
              <div className="gate-decision-form">
                <h3>Gate 2b: Commit to Work + Lock Priority</h3>
                <div className="form-group">
                  <label>Decision:</label>
                  <select value={gateDecision} onChange={(e) => setGateDecision(e.target.value as 'Approved' | 'Rejected')}>
                    <option value="Approved">Approve</option>
                    <option value="Rejected">Reject</option>
                  </select>
                </div>
                {gateDecision === 'Approved' && (
                  <div className="form-group">
                    <label>Priority:</label>
                    <select value={priority} onChange={(e) => setPriority(e.target.value as any)}>
                      <option value="P1">P1 (Critical)</option>
                      <option value="P2">P2 (High)</option>
                      <option value="P3">P3 (Medium)</option>
                      <option value="P4">P4 (Low)</option>
                    </select>
                  </div>
                )}
                {error && <div className="error-message">{error}</div>}
                <div className="form-actions">
                  <button onClick={() => handleGateDecision(3)} className="btn-primary">Submit Gate 2b</button>
                  <button onClick={() => setShowGateDecision(false)} className="btn-secondary">Cancel</button>
                </div>
              </div>
            )
          ) : null}
        </div>
      </div>
    </div>
  );
}
